import { AgentMarkdownLink } from "~/components/ui/agent-markdown-link";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import type {
  AssistantTurnData,
  Turn,
} from "~/pages/manifold-nextgen/components/conversations/useTurns";

const MARKDOWN_COMPONENTS = { a: AgentMarkdownLink };

interface PlatformTurnsProps {
  turns: Turn[];
}

// The help panel's turns: plain bubbles, without the session chat's tool
// disclosures, feedback and artifacts.
export function PlatformTurns({ turns }: PlatformTurnsProps) {
  return (
    <>
      {turns.map((turn) => {
        if (turn.kind === "user") {
          return (
            <li key={turn.key} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br bg-primary px-3 py-2 text-[13px] text-primary-foreground">
                <p className="whitespace-pre-wrap">{turn.text}</p>
              </div>
            </li>
          );
        }
        // One agent per session, so there are no handoff markers.
        if (turn.kind !== "assistant") return null;
        return <AssistantTurn key={turn.key} turn={turn} />;
      })}
    </>
  );
}

const AssistantTurn = ({ turn }: { turn: AssistantTurnData }) => {
  const hasAnswer = Boolean(turn.text) || turn.cancelled || turn.failed;
  // A settled turn with no answer would render as an empty row.
  if (!turn.isRunning && !hasAnswer) return null;

  return (
    <li className="flex justify-start">
      <div className="flex max-w-[85%] min-w-0 flex-col gap-1">
        {turn.isRunning && (
          <p className="shimmer-text px-3 text-xs" role="status">
            Thinking
          </p>
        )}
        {hasAnswer && (
          <div className="min-w-0 rounded-2xl rounded-tl border border-border bg-muted px-3 py-2">
            {turn.text && (
              <MarkdownPreview className="text-[13px]" components={MARKDOWN_COMPONENTS}>
                {turn.text}
              </MarkdownPreview>
            )}
            {turn.cancelled && <TurnNotice>Response cancelled</TurnNotice>}
            {turn.failed && (
              <TurnNotice destructive>
                {turn.failureMessage ?? "Something went wrong. Try asking again."}
              </TurnNotice>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

const TurnNotice = ({
  destructive,
  children,
}: {
  destructive?: boolean;
  children: React.ReactNode;
}) => (
  <p className={destructive ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
    {children}
  </p>
);
